import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SHOP_PUBLIC_COLUMNS } from '../lib/shopColumns';
import { resolveSubdomainFromUrl } from '../lib/resolveShop';
import ShopThemed, { normaliseTheme, resolveAccent } from '../themes';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_ORDER  = [1, 2, 3, 4, 5, 6, 0]; // display Mon..Sun

/**
 * Convert the stored `shops.opening_hours` JSONB (or the legacy union-
 * of-barber-hours fallback) into the display shape the theme expects:
 * [{ label, closed, start, end }] in Mon..Sun order.
 */
const openingHoursToDisplay = (rows) =>
  (rows || []).map((r) => ({
    label: DAY_LABELS[r.weekday],
    closed: !!r.closed,
    start: r.closed ? null : (r.open_time || '').slice(0, 5),
    end:   r.closed ? null : (r.close_time || '').slice(0, 5),
  }));

/**
 * Legacy fallback for shops that never opened /profile after the M4
 * migration — union of barber hours (earliest start → latest end).
 * If no barber is open on a day, that day is "Closed".
 */
const computeShopHours = (barberHourRows) => {
  const byDay = {};
  for (const r of barberHourRows) {
    if (!r.is_open) continue;
    const d = r.weekday;
    if (!byDay[d]) byDay[d] = { start: r.start_time, end: r.end_time };
    else {
      if (r.start_time < byDay[d].start) byDay[d].start = r.start_time;
      if (r.end_time   > byDay[d].end)   byDay[d].end   = r.end_time;
    }
  }
  return DAY_ORDER.map(d => ({
    label: DAY_LABELS[d],
    closed: !byDay[d],
    start: byDay[d]?.start,
    end:   byDay[d]?.end,
  }));
};

const ShopHome = () => {
  const { subdomain: pathSubdomain } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [shop, setShop] = useState(null);
  const [shopNotFound, setShopNotFound] = useState(false);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setShopNotFound(false);
      try {
        const subdomain = resolveSubdomainFromUrl({
          hostname: typeof window !== 'undefined' ? window.location.hostname : '',
          pathname: location.pathname,
          search: location.search,
          pathSubdomain,
        });

        let shopQuery = supabase.from('shops').select(SHOP_PUBLIC_COLUMNS);
        if (subdomain) shopQuery = shopQuery.eq('subdomain', subdomain).single();
        else           shopQuery = shopQuery.limit(1).single();
        const { data: shopData, error: shopError } = await shopQuery;

        if (shopError || !shopData) { setShopNotFound(true); return; }
        setShop(shopData);

        const [{ data: svcs }, { data: brbs }] = await Promise.all([
          supabase.rpc('get_public_shop_services', { _shop_id: shopData.id }),
          supabase.rpc('get_public_shop_barbers',  { _shop_id: shopData.id }),
        ]);
        setServices(svcs || []);
        setBarbers(brbs || []);

        const barberIds = (brbs || []).map(b => b.id);
        // M4: prefer shop-level opening hours when set (via /profile);
        // otherwise fall back to the union-of-barbers computation so
        // shops that never opened the editor keep rendering.
        if (Array.isArray(shopData.opening_hours) && shopData.opening_hours.length) {
          setHours(openingHoursToDisplay(shopData.opening_hours));
        } else if (barberIds.length) {
          const { data: bhrs } = await supabase
            .from('barber_hours').select('weekday,start_time,end_time,is_open')
            .in('barber_id', barberIds);
          setHours(computeShopHours(bhrs || []));
        } else {
          setHours([]);
        }
      } catch (err) {
        console.error('ShopHome load error:', err);
        setShopNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathSubdomain, location.search]);

  // Push the shop's saved accent_colour into the CSS custom properties
  // used by the Instacut default theme so the same colour that shows in
  // the admin's palette picker also shows on the public shop-front —
  // previously the theme was reading `--acc` from :root only, which
  // meant the shop's chosen accent applied to the admin surfaces but
  // NOT to their own landing page (reported by manual test SP-accent).
  useEffect(() => {
    if (!shop) return;
    const theme = normaliseTheme(shop.theme);
    const accent = resolveAccent(theme, shop.accent_colour);
    if (!accent) return;
    const root = document.documentElement;
    const prev = {
      acc: root.style.getPropertyValue('--acc'),
      soft: root.style.getPropertyValue('--acc-soft'),
      ink: root.style.getPropertyValue('--acc-ink'),
    };
    root.style.setProperty('--acc', accent);
    // Same soft-mix + text-on-accent logic Booking.js uses so hover /
    // link backgrounds stay legible against the chosen colour.
    root.style.setProperty('--acc-soft', `color-mix(in srgb, ${accent} 12%, white)`);
    root.style.setProperty('--acc-ink', `color-mix(in srgb, ${accent} 82%, black)`);
    return () => {
      root.style.setProperty('--acc', prev.acc);
      root.style.setProperty('--acc-soft', prev.soft);
      root.style.setProperty('--acc-ink', prev.ink);
    };
  }, [shop?.accent_colour, shop?.theme]);

  const goBook = (serviceId) => {
    const sub = shop?.subdomain;
    if (!sub) return;
    const qs = serviceId ? `?service=${encodeURIComponent(serviceId)}` : '';
    navigate(`/book/${sub}${qs}`);
  };

  if (loading) {
    return (
      <div className="theme-clean-editorial">
        <div className="shell"><div className="notfound"><p>Loading…</p></div></div>
      </div>
    );
  }

  if (shopNotFound || !shop) {
    return (
      <div className="theme-clean-editorial" data-testid="shop-not-found">
        <div className="shell">
          <div className="notfound">
            <h1>Shop not found</h1>
            <p>The booking link you followed doesn&apos;t match a live shop.</p>
          </div>
        </div>
      </div>
    );
  }

  // Render the theme stored on the shop record.
  const theme = normaliseTheme(shop.theme);

  return (
    <ShopThemed
      theme={theme}
      shop={shop}
      services={services}
      barbers={barbers}
      hours={hours}
      showHours={shop.show_hours !== false}
      onBook={goBook}
      allowPortal
    />
  );
};

export default ShopHome;
